import { RefObject, useEffect, useState } from 'react';

interface ScrollAffordance {
  canScrollUp: boolean;
  canScrollDown: boolean;
}

/**
 * Reports whether a scroll container has content hidden above/below the fold.
 * Recomputes on scroll and on size changes via a ResizeObserver.
 */
const useScrollAffordance = (ref: RefObject<HTMLElement>): ScrollAffordance => {
  const [affordance, setAffordance] = useState<ScrollAffordance>({
    canScrollUp: false,
    canScrollDown: false,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setAffordance({
        canScrollUp: scrollTop > 0,
        // 1px slack for sub-pixel rounding at the bottom.
        canScrollDown: scrollTop + clientHeight < scrollHeight - 1,
      });
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [ref]);

  return affordance;
};

export default useScrollAffordance;
